import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface Review {
  id: number;
  product_name: string;
  customer_name: string;
  rating: number;
  comment: string;
}

const AdminReviews: React.FC = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await fetch(`${API_URL}/reviews`, {
          credentials: "include",
        });

        if (res.status === 401) {
          navigate("/admin/login");
          return;
        }

        const data = await res.json();
        setReviews(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [API_URL, navigate]);

  /* ================= LOADING ================= */

  if (loading) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 text-gray-600">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-300 border-t-indigo-600" />
        <p className="text-sm">Loading reviews…</p>
      </div>
    );
  }

  /* ================= UI ================= */

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-900">
            Product Reviews
          </h2>
          <p className="text-sm text-gray-500">
            Customer feedback on products
          </p>
        </div>

        {/* Table Card */}
        <div className="overflow-hidden rounded-xl border-2 border-gray-300 bg-white shadow-sm">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="border-b-2 border-r border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">
                  Product
                </th>
                <th className="border-b-2 border-r border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">
                  Customer
                </th>
                <th className="border-b-2 border-r border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">
                  Rating
                </th>
                <th className="border-b-2 border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">
                  Comment
                </th>
              </tr>
            </thead>

            <tbody>
              {reviews.length ? (
                reviews.map((r) => (
                  <tr
                    key={r.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="border-b border-r border-gray-300 px-4 py-3">
                      {r.product_name}
                    </td>
                    <td className="border-b border-r border-gray-300 px-4 py-3">
                      {r.customer_name}
                    </td>
                    <td className="border-b border-r border-gray-300 px-4 py-3">
                      <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 font-medium text-yellow-800">
                        {r.rating} ★
                      </span>
                    </td>
                    <td className="border-b border-gray-300 px-4 py-3 text-gray-700">
                      {r.comment}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-10 text-center text-gray-500"
                  >
                    No reviews available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminReviews;
